import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

import QRCode from 'qrcode';

async function generateVotersListPages(
  pdfDoc: PDFDocument, 
  voters: any[], 
  font: any, 
  boldFont: any,
  precinct: string,
  barangay: string,
  municipality: string
): Promise<void> {
  // Letter bond paper size in points (8.5 x 11 inches)
  const WIDTH = 612;  // 8.5 * 72
  const HEIGHT = 792; // 11 * 72

  // Margins and layout
  const MARGIN = 30;
  const QR_SIZE = 60;
  const LINE_HEIGHT = 15;

  let currentPage = pdfDoc.addPage([WIDTH, HEIGHT]);
  let yPosition = HEIGHT - MARGIN;

  // Add header with precinct, barangay, and municipality
  currentPage.drawText(`Precinct: ${precinct} - ${barangay}, ${municipality}`, {
    x: MARGIN,
    y: HEIGHT - 40,
    size: 12,
    font: boldFont,
    color: rgb(0, 0, 0)
  });

  // Horizontal line under header
  currentPage.drawLine({
    start: { x: MARGIN, y: HEIGHT - 45 },
    end: { x: WIDTH - MARGIN, y: HEIGHT - 45 },
    thickness: 1,
    color: rgb(0, 0, 0)
  });

  // Adjust initial yPosition to account for header
  yPosition = HEIGHT - 60;

  // Iterate through voters for list pages
  for (let i = 0; i < voters.length; i++) {
    // Check if we need a new page
    if (yPosition < MARGIN + QR_SIZE) {
      currentPage = pdfDoc.addPage([WIDTH, HEIGHT]);
      yPosition = HEIGHT - 60;

      // Add header to new page
      currentPage.drawText(`Precinct: ${precinct} | Barangay: ${barangay} | Municipality: ${municipality}`, {
        x: MARGIN,
        y: HEIGHT - 40,
        size: 12,
        font: boldFont,
        color: rgb(0, 0, 0)
      });

      // Horizontal line under header
      currentPage.drawLine({
        start: { x: MARGIN, y: HEIGHT - 45 },
        end: { x: WIDTH - MARGIN, y: HEIGHT - 45 },
        thickness: 1,
        color: rgb(0, 0, 0)
      });
    }

    const voter = voters[i];

    // Generate QR Code for voter list
    const qrCodeDataUrl = await QRCode.toDataURL(voter.id, { 
      width: QR_SIZE, 
      margin: 1 
    });

    // Embed QR Code image
    const qrCodeImage = await pdfDoc.embedPng(
      await fetch(qrCodeDataUrl).then(r => r.arrayBuffer())
    );

    // Draw QR Code (slightly adjusted to align better with name)
    currentPage.drawImage(qrCodeImage, {
      x: MARGIN,
      y: yPosition - QR_SIZE + 4,
      width: QR_SIZE,
      height: QR_SIZE
    });

    // Draw Voter Details (3 lines)
    // Line 1: Voter Name
    currentPage.drawText(voter.name, {
      x: MARGIN + QR_SIZE + 20,
      y: yPosition - 10,
      size: 12,
      font: boldFont,
      color: rgb(0, 0, 0)
    });

    // Line 2: Voter ID
    currentPage.drawText(voter.id, {
      x: MARGIN + QR_SIZE + 20,
      y: yPosition - LINE_HEIGHT - 10,
      size: 10,
      font: font,
      color: rgb(0.3, 0.3, 0.3)
    });

    // Line 3: Precinct, Barangay, Municipality
    currentPage.drawText(`${voter.precinct} | ${voter.barangay} | ${voter.city_municipality}`, {
      x: MARGIN + QR_SIZE + 20,
      y: yPosition - (LINE_HEIGHT * 2) - 10,
      size: 10,
      font: font,
      color: rgb(0.3, 0.3, 0.3)
    });

    // Draw horizontal line between voters
    currentPage.drawLine({
      start: { x: MARGIN, y: yPosition - QR_SIZE - 10 },
      end: { x: WIDTH - MARGIN, y: yPosition - QR_SIZE - 10 },
      thickness: 0.5,
      color: rgb(0.7, 0.7, 0.7)
    });

    // Move to next position
    yPosition -= QR_SIZE + 23;
  }
}

async function generateVoterTicketPages(
  pdfDoc: PDFDocument, 
  voters: any[], 
  font: any, 
  boldFont: any
): Promise<void> {
  // Letter bond paper size in points (8.5 x 11 inches)
  const WIDTH = 612;  // 8.5 * 72
  const HEIGHT = 792; // 11 * 72

  // Margins and layout
  const MARGIN = 15;
  const QR_SIZE = 60;
  const LINE_HEIGHT = 15;
  const COLUMN_SPACING = 0;

  let currentPage = pdfDoc.addPage([WIDTH, HEIGHT]);
  let yPosition = HEIGHT - MARGIN;

  // Iterate through voters for ticket pages
  for (let i = 0; i < voters.length; i++) {
    // Check if we need a new page
    if (yPosition < MARGIN + QR_SIZE) {
      currentPage = pdfDoc.addPage([WIDTH, HEIGHT]);
      yPosition = HEIGHT - MARGIN;
    }

    // Left Column Voter
    const leftVoter = voters[i];
    const leftQRCodeDataUrl = await QRCode.toDataURL(leftVoter.id, { 
      width: QR_SIZE, 
      margin: 1 
    });
    const leftQRCodeImage = await pdfDoc.embedPng(
      await fetch(leftQRCodeDataUrl).then(r => r.arrayBuffer())
    );

    // Draw Left QR Code
    currentPage.drawImage(leftQRCodeImage, {
      x: MARGIN,
      y: yPosition - QR_SIZE + 4,
      width: QR_SIZE,
      height: QR_SIZE
    });

    // Draw Left Voter Details
    currentPage.drawText(leftVoter.name, {
      x: MARGIN + QR_SIZE + 10,
      y: yPosition - 10,
      size: 10,
      font: boldFont,
      color: rgb(0, 0, 0)
    });

    currentPage.drawText(leftVoter.id, {
      x: MARGIN + QR_SIZE + 10,
      y: yPosition - LINE_HEIGHT - 10,
      size: 10,
      font: font,
      color: rgb(0.3, 0.3, 0.3)
    });

    currentPage.drawText(`${leftVoter.precinct} | ${leftVoter.barangay} | ${leftVoter.city_municipality}`, {
      x: MARGIN + QR_SIZE + 10,
      y: yPosition - (LINE_HEIGHT * 2) - 10,
      size: 10,
      font: font,
      color: rgb(0.3, 0.3, 0.3)
    });

    // Right Column Voter (if exists)
    const rightVoter = voters[i];
    const rightQRCodeDataUrl = await QRCode.toDataURL(rightVoter.id, { 
      width: QR_SIZE, 
      margin: 1 
    });
    const rightQRCodeImage = await pdfDoc.embedPng(
      await fetch(rightQRCodeDataUrl).then(r => r.arrayBuffer())
    );

    // Draw Right QR Code
    currentPage.drawImage(rightQRCodeImage, {
      x: WIDTH / 2 + COLUMN_SPACING,
      y: yPosition - QR_SIZE + 4,
      width: QR_SIZE,
      height: QR_SIZE
    });

    // Draw Right Voter Details
    currentPage.drawText(rightVoter.name, {
      x: WIDTH / 2 + COLUMN_SPACING + QR_SIZE + 10,
      y: yPosition - 10,
      size: 10,
      font: boldFont,
      color: rgb(0, 0, 0)
    });

    currentPage.drawText(rightVoter.id, {
      x: WIDTH / 2 + COLUMN_SPACING + QR_SIZE + 10,
      y: yPosition - LINE_HEIGHT - 10,
      size: 10,
      font: font,
      color: rgb(0.3, 0.3, 0.3)
    });

    currentPage.drawText(`${rightVoter.precinct} | ${rightVoter.barangay} | ${rightVoter.city_municipality}`, {
      x: WIDTH / 2 + COLUMN_SPACING + QR_SIZE + 10,
      y: yPosition - (LINE_HEIGHT * 2) - 10,
      size: 10,
      font: font,
      color: rgb(0.3, 0.3, 0.3)
    });
  
    // Draw horizontal line between rows
    currentPage.drawLine({
      start: { x: MARGIN, y: yPosition - QR_SIZE - 10 },
      end: { x: WIDTH - MARGIN, y: yPosition - QR_SIZE - 10 },
      thickness: 0.5,
      color: rgb(0.7, 0.7, 0.7)
    });

    // Move to next position
    yPosition -= QR_SIZE + 23;
  }
}

export async function generateCombinedVoterPDF(
  voters: any[], 
  precinct: string, 
  barangay: string, 
  municipality: string
): Promise<Blob> {
  // Create a new PDF document
  const pdfDoc = await PDFDocument.create();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  // Generate voters list pages
  await generateVotersListPages(pdfDoc, voters, font, boldFont, precinct, barangay, municipality);

  // Generate voter ticket pages (side-by-side)
  await generateVoterTicketPages(pdfDoc, voters, font, boldFont);

  // Serialize PDF to bytes
  const pdfBytes = await pdfDoc.save();

  // Create Blob
  return new Blob([pdfBytes], { type: 'application/pdf' });
}
